import React, { useState, useMemo, useEffect } from 'react'
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Typography,
  Skeleton,
  Alert,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  FormControl,
  InputLabel,
  Select,
  Grid,
  Button
} from '@mui/material'
import {
  Search,
  Edit,
  Delete,
  Visibility,
  MoreVert,
  ShoppingCart,
  PictureAsPdf,
  CheckCircle,
  Schedule,
  Warning,
  FilterList,
  Clear,
  ArrowUpward,
  ArrowDownward,
  UnfoldMore,
  Repeat
} from '@mui/icons-material'

import { usePurchaseOrders, useUpdatePurchaseOrderStatus, useDeletePurchaseOrder } from '../../hooks/usePurchaseOrders'
import { useClients } from '../../hooks/useClients'
import { useQueryClient } from 'react-query'
import { formatCurrency, formatDate } from '../../utils/formatters'
import useAuthStore from '../../store/authStore'
import RoleGuard from '../Auth/RoleGuard'
import PdfViewer from '../Common/PdfViewer'
import { purchaseOrdersAPI } from '../../utils/api'

const PurchaseOrdersTable = ({ onEdit, onView }) => {
  const { isAdmin } = useAuthStore()
  const queryClient = useQueryClient()
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [clientFilter, setClientFilter] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  const [sortBy, setSortBy] = useState('issueDate')
  const [sortOrder, setSortOrder] = useState('desc')
  const [anchorEl, setAnchorEl] = useState(null)
  const [selectedPurchaseOrder, setSelectedPurchaseOrder] = useState(null)
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false)
  const [currentPdfUrl, setCurrentPdfUrl] = useState(null)
  const [currentPdfTitle, setCurrentPdfTitle] = useState('')

  const updateStatusMutation = useUpdatePurchaseOrderStatus()
  const deletePurchaseOrderMutation = useDeletePurchaseOrder()

  // Get clients for filter
  const { data: clientsData } = useClients({ page: 1, limit: 100 })

  // Query parameters
  const queryParams = useMemo(() => ({
    page: page + 1,
    limit: rowsPerPage,
    search: search.trim(),
    status: statusFilter,
    clientId: clientFilter,
    ocType: typeFilter,
    sortBy: sortBy,
    sortOrder: sortOrder
  }), [page, rowsPerPage, search, statusFilter, clientFilter, typeFilter, sortBy, sortOrder])

  const { data, isLoading, error, refetch } = usePurchaseOrders(queryParams)

  // Refetch data when component mounts or window gains focus
  useEffect(() => {
    const handleFocus = () => {
      refetch()
    }

    window.addEventListener('focus', handleFocus)
    
    // Initial refetch
    refetch()

    return () => {
      window.removeEventListener('focus', handleFocus)
    }
  }, [refetch])

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleSearchChange = (event) => {
    setSearch(event.target.value)
    setPage(0)
  }

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value)
    setPage(0)
  }

  const handleClientFilterChange = (event) => {
    setClientFilter(event.target.value)
    setPage(0)
  }

  const handleTypeFilterChange = (event) => {
    setTypeFilter(event.target.value)
    setPage(0)
  }

  const handleClearFilters = () => {
    setSearch('')
    setStatusFilter('')
    setClientFilter('')
    setTypeFilter('')
    setPage(0)
  }

  const handleSort = (column) => {
    if (sortBy === column) {
      // Toggle sort order if same column
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      // Set new column and default to ascending
      setSortBy(column)
      setSortOrder('asc')
    }
    setPage(0) // Reset to first page when sorting
  }

  const getSortIcon = (column) => {
    if (sortBy !== column) {
      return <UnfoldMore fontSize="small" sx={{ opacity: 0.5 }} />
    }
    return sortOrder === 'asc' 
      ? <ArrowUpward fontSize="small" />
      : <ArrowDownward fontSize="small" />
  }

  // Sortable header component
  const SortableHeader = ({ column, children, align = 'left', sortable = true }) => {
    if (!sortable) {
      return <TableCell align={align}>{children}</TableCell>
    }

    return (
      <TableCell 
        align={align}
        sx={{ 
          cursor: 'pointer',
          userSelect: 'none',
          '&:hover': {
            backgroundColor: 'action.hover'
          }
        }}
        onClick={() => handleSort(column)}
      >
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          justifyContent: align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start',
          gap: 0.5
        }}>
          {children}
          {getSortIcon(column)}
        </Box>
      </TableCell>
    )
  }

  const handleMenuOpen = (event, purchaseOrder) => {
    setAnchorEl(event.currentTarget)
    setSelectedPurchaseOrder(purchaseOrder)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
    setSelectedPurchaseOrder(null)
  }

  const handleEdit = () => {
    if (selectedPurchaseOrder && onEdit) {
      onEdit(selectedPurchaseOrder)
    }
    handleMenuClose()
  }

  const handleView = () => {
    if (selectedPurchaseOrder && onView) {
      onView(selectedPurchaseOrder)
    }
    handleMenuClose()
  }

  const handleViewPdf = async () => {
    if (selectedPurchaseOrder?.pdfKey) {
      try {
        const response = await purchaseOrdersAPI.getPdfUrl(selectedPurchaseOrder.id)
        if (response.signedUrl) {
          setCurrentPdfUrl(response.signedUrl)
          setCurrentPdfTitle(`Orden de Compra ${selectedPurchaseOrder.ocNumber}`)
          setPdfViewerOpen(true)
        }
      } catch (error) {
        console.error('Error getting PDF URL:', error)
      }
    }
    handleMenuClose()
  }

  // Direct PDF handler for table button (doesn't use selectedPurchaseOrder)
  const handleDirectViewPdf = async (purchaseOrder) => {
    if (purchaseOrder?.pdfKey) {
      try {
        const response = await purchaseOrdersAPI.getPdfUrl(purchaseOrder.id)
        if (response.signedUrl) {
          setCurrentPdfUrl(response.signedUrl)
          setCurrentPdfTitle(`Orden de Compra ${purchaseOrder.ocNumber}`)
          setSelectedPurchaseOrder(purchaseOrder) // Set for the modal
          setPdfViewerOpen(true)
        }
      } catch (error) {
        console.error('Error getting PDF URL:', error)
      }
    }
  }

  const handleStatusChange = async (newStatus) => {
    if (selectedPurchaseOrder) {
      try {
        await updateStatusMutation.mutateAsync({
          id: selectedPurchaseOrder.id,
          status: newStatus
        })
        refetch()
      } catch (error) {
        console.error('Error updating status:', error)
      }
    }
    handleMenuClose()
  }

  const handleDelete = async () => {
    if (selectedPurchaseOrder) {
      try {
        await deletePurchaseOrderMutation.mutateAsync(selectedPurchaseOrder.id)
        refetch()
      } catch (error) {
        console.error('Error deleting purchase order:', error)
      }
    }
    handleMenuClose()
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'POR_FACTURAR': return 'warning'
      case 'FACTURADA': return 'success'
      default: return 'default'
    }
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'POR_FACTURAR': return <Schedule fontSize="small" />
      case 'FACTURADA': return <CheckCircle fontSize="small" />
      default: return <ShoppingCart fontSize="small" />
    }
  }

  const getTypeIcon = (type) => {
    return type === 'PERIODICA' ? <Repeat fontSize="small" /> : <ShoppingCart fontSize="small" />
  }

  const getTypeColor = (type) => {
    return type === 'PERIODICA' ? 'primary' : 'default'
  }

  if (error) {
    return (
      <Alert 
        severity="error" 
        action={
          <IconButton size="small" onClick={() => refetch()}>
            <Search />
          </IconButton>
        }
      >
        Error cargando órdenes de compra: {error.message}
      </Alert>
    )
  }

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      {/* Filters Bar */}
      <Box sx={{ p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              placeholder="Buscar por número, cliente o descripción..."
              value={search}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Estado</InputLabel>
              <Select
                value={statusFilter}
                onChange={handleStatusFilterChange}
                label="Estado"
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="POR_FACTURAR">Por Facturar</MenuItem>
                <MenuItem value="FACTURADA">Facturada</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Tipo</InputLabel>
              <Select
                value={typeFilter}
                onChange={handleTypeFilterChange}
                label="Tipo"
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="UNICA">Única</MenuItem>
                <MenuItem value="PERIODICA">Periódica</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Cliente</InputLabel>
              <Select
                value={clientFilter}
                onChange={handleClientFilterChange}
                label="Cliente"
              >
                <MenuItem value="">Todos los clientes</MenuItem>
                {clientsData?.clients?.map((client) => (
                  <MenuItem key={client.id} value={client.id}>
                    {client.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={2}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<Clear />}
                onClick={handleClearFilters}
                size="small"
              >
                Limpiar
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Table */}
      <TableContainer>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <SortableHeader column="ocNumber">OC</SortableHeader>
              <SortableHeader column="clientName">Cliente</SortableHeader>
              <SortableHeader column="description" sortable={false}>Descripción</SortableHeader>
              <SortableHeader column="issueDate" align="center">Emisión</SortableHeader>
              <SortableHeader column="ocType" align="center">Tipo</SortableHeader>
              <SortableHeader column="totalAmount" align="right">Monto</SortableHeader>
              <SortableHeader column="status" align="center">Estado</SortableHeader>
              <SortableHeader column="" align="center" sortable={false}>PDF</SortableHeader>
              <SortableHeader column="" align="center" sortable={false}>Acciones</SortableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              // Loading skeletons
              Array.from({ length: rowsPerPage }).map((_, index) => (
                <TableRow key={index}>
                  <TableCell><Skeleton variant="text" width={100} /></TableCell>
                  <TableCell><Skeleton variant="text" width={120} /></TableCell>
                  <TableCell><Skeleton variant="text" width={150} /></TableCell>
                  <TableCell><Skeleton variant="text" width={80} /></TableCell>
                  <TableCell><Skeleton variant="rectangular" width={80} height={24} /></TableCell>
                  <TableCell><Skeleton variant="text" width={100} /></TableCell>
                  <TableCell><Skeleton variant="rectangular" width={80} height={24} /></TableCell>
                  <TableCell><Skeleton variant="circular" width={24} height={24} /></TableCell>
                  <TableCell><Skeleton variant="circular" width={32} height={32} /></TableCell>
                </TableRow>
              ))
            ) : data?.purchaseOrders?.length > 0 ? (
              data.purchaseOrders.map((purchaseOrder) => (
                <TableRow 
                  key={purchaseOrder.id} 
                  hover
                  sx={{ '&:hover': { backgroundColor: 'action.hover' } }}
                >
                  {/* OC Number */}
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {purchaseOrder.ocNumber}
                    </Typography>
                  </TableCell>

                  {/* Client */}
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {purchaseOrder.client?.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {purchaseOrder.client?.rut}
                      </Typography>
                    </Box>
                  </TableCell>

                  {/* Description */}
                  <TableCell>
                    <Tooltip title={purchaseOrder.description}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          maxWidth: 200,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {purchaseOrder.description}
                      </Typography>
                    </Tooltip>
                  </TableCell>

                  {/* Issue Date */}
                  <TableCell align="center">
                    <Typography variant="body2">
                      {formatDate(purchaseOrder.issueDate)}
                    </Typography>
                  </TableCell>

                  {/* Type */}
                  <TableCell align="center">
                    <Chip
                      label={purchaseOrder.ocType === 'PERIODICA' ? 'Periódica' : 'Única'}
                      color={getTypeColor(purchaseOrder.ocType)}
                      size="small"
                      variant="outlined"
                      icon={getTypeIcon(purchaseOrder.ocType)}
                    />
                    {purchaseOrder.isPeriodic && purchaseOrder.billingDay && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        Día {purchaseOrder.billingDay}
                      </Typography>
                    )}
                  </TableCell>

                  {/* Amount */}
                  <TableCell align="right">
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {formatCurrency(purchaseOrder.totalAmount)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Neto: {formatCurrency(purchaseOrder.netAmount)}
                      </Typography>
                    </Box>
                  </TableCell>

                  {/* Status */}
                  <TableCell align="center">
                    <Chip
                      label={purchaseOrder.status}
                      color={getStatusColor(purchaseOrder.status)}
                      size="small"
                      variant="outlined"
                      icon={getStatusIcon(purchaseOrder.status)}
                    />
                  </TableCell>

                  {/* PDF */}
                  <TableCell align="center">
                    {purchaseOrder.pdfKey ? (
                      <Tooltip title="Ver PDF">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleDirectViewPdf(purchaseOrder)
                          }}
                          color="primary"
                        >
                          <PictureAsPdf />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Sin PDF">
                        <IconButton size="small" disabled>
                          <PictureAsPdf />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>

                  {/* Actions */}
                  <TableCell align="center">
                    <Tooltip title="Más opciones">
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, purchaseOrder)}
                      >
                        <MoreVert />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <ShoppingCart sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                    <Typography variant="h6" color="text.secondary">
                      No se encontraron órdenes de compra
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {search || statusFilter || clientFilter || typeFilter
                        ? 'Intenta con otros filtros de búsqueda' 
                        : 'Aún no hay órdenes de compra registradas'
                      }
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {data && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={data.pagination?.total || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) => 
            `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
          }
        />
      )}

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleView}>
          <ListItemIcon>
            <Visibility fontSize="small" />
          </ListItemIcon>
          <ListItemText>Ver detalles</ListItemText>
        </MenuItem>

        {selectedPurchaseOrder?.pdfKey && (
          <MenuItem onClick={handleViewPdf}>
            <ListItemIcon>
              <PictureAsPdf fontSize="small" />
            </ListItemIcon>
            <ListItemText>Ver PDF</ListItemText>
          </MenuItem>
        )}

        <RoleGuard requireAdmin fallback={null}>
          <MenuItem onClick={handleEdit}>
            <ListItemIcon>
              <Edit fontSize="small" />
            </ListItemIcon>
            <ListItemText>Editar</ListItemText>
          </MenuItem>

          {/* Status change options */}
          {selectedPurchaseOrder?.status !== 'POR_FACTURAR' && (
            <MenuItem onClick={() => handleStatusChange('POR_FACTURAR')}>
              <ListItemIcon>
                <Schedule fontSize="small" color="warning" />
              </ListItemIcon>
              <ListItemText>Marcar como Por Facturar</ListItemText>
            </MenuItem>
          )}

          {selectedPurchaseOrder?.status !== 'FACTURADA' && (
            <MenuItem onClick={() => handleStatusChange('FACTURADA')}>
              <ListItemIcon>
                <CheckCircle fontSize="small" color="success" />
              </ListItemIcon>
              <ListItemText>Marcar como Facturada</ListItemText>
            </MenuItem>
          )}

          <MenuItem 
            onClick={handleDelete}
            disabled={deletePurchaseOrderMutation.isLoading}
            sx={{ color: 'error.main' }}
          >
            <ListItemIcon>
              <Delete fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Eliminar</ListItemText>
          </MenuItem>
        </RoleGuard>
      </Menu>

      {/* PDF Viewer Modal */}
      <PdfViewer
        open={pdfViewerOpen}
        onClose={() => setPdfViewerOpen(false)}
        pdfUrl={currentPdfUrl}
        title={currentPdfTitle}
        fileName={`${selectedPurchaseOrder?.ocNumber || 'orden-compra'}.pdf`}
      />
    </Paper>
  )
}

export default PurchaseOrdersTable
